import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import {
    Search, Plus, Filter, User, X, Check,
    ChevronDown, MoreVertical, Calendar, UserPlus,
    Target, Activity, ChevronRight, HeartPulse
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_OPTIONS = ['All', 'Active Evaluation', 'Ongoing Intervention', 'Discharged'];
const SUPPORT_OPTIONS = ['All', 'Level 1', 'Level 2', 'Level 3'];

export default function Profiles() {
    const { profiles, addProfile } = useData();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('All');
    const [supportFilter, setSupportFilter] = useState('All');
    const [showOnlyMyPatients, setShowOnlyMyPatients] = useState(user?.role === 'Therapist');

    const filteredProfiles = profiles.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
            p.diagnosis.toLowerCase().includes(search.toLowerCase());
        const matchesStatus = statusFilter === 'All' || p.status === statusFilter;
        const matchesSupport = supportFilter === 'All' || p.supportLevel === supportFilter;
        const matchesAssignment = !showOnlyMyPatients || p.assignedTherapistId === user?.id;
        return matchesSearch && matchesStatus && matchesSupport && matchesAssignment;
    });

    const handleAdd = (e) => {
        e.preventDefault();
        const data = new FormData(e.target);
        addProfile({
            name: data.get('name'),
            age: data.get('age'),
            diagnosis: data.get('diagnosis'),
            supportLevel: data.get('supportLevel'),
            pcMainConcern: data.get('pcMainConcern'),
            status: 'Active Evaluation'
        });
        setAddModalOpen(false);
    };

    return (
        <div className="space-y-10">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 font-outfit tracking-tight">Patient Directory</h1>
                    <p className="text-slate-500 mt-2 text-lg font-light">Managing clinical profiles and growth trajectories.</p>
                </div>
                <Button onClick={() => setAddModalOpen(true)} className="rounded-2xl h-14 px-8 shadow-premium font-black text-sm uppercase tracking-widest bg-brand-600 hover:bg-brand-700 text-white transition-all">
                    <UserPlus className="mr-3 h-5 w-5" /> New Registration
                </Button>
            </div>

            {/* Filter & Search Dashboard */}
            <Card className="border-0 shadow-soft overflow-hidden rounded-[2.5rem] bg-white border border-slate-50">
                <CardContent className="p-8 space-y-8">
                    <div className="flex flex-col lg:flex-row gap-6">
                        <div className="relative flex-1 group">
                            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-300 group-focus-within:text-brand-500 transition-colors" />
                            <input
                                className="w-full h-14 pl-14 pr-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand-500 text-slate-900 placeholder:text-slate-300 font-medium transition-all"
                                placeholder="Search by name, clinical marker, or diagnosis..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="flex gap-4">
                            <div className="relative">
                                <select
                                    className="h-14 pl-6 pr-12 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand-500 text-slate-900 font-bold text-sm appearance-none cursor-pointer min-w-[180px]"
                                    value={statusFilter}
                                    onChange={e => setStatusFilter(e.target.value)}
                                >
                                    {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt === 'All' ? 'Status: All' : opt}</option>)}
                                </select>
                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                            </div>
                            <div className="relative">
                                <select
                                    className="h-14 pl-6 pr-12 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand-500 text-slate-900 font-bold text-sm appearance-none cursor-pointer min-w-[180px]"
                                    value={supportFilter}
                                    onChange={e => setSupportFilter(e.target.value)}
                                >
                                    {SUPPORT_OPTIONS.map(opt => <option key={opt} value={opt}>{opt === 'All' ? 'Support: All' : opt}</option>)}
                                </select>
                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                            </div>
                        </div>
                    </div>

                    <div className="pt-6 border-t border-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                                <span className={`text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${showOnlyMyPatients ? 'text-brand-600' : 'text-slate-400'}`}>My Patients Only</span>
                                <button
                                    onClick={() => setShowOnlyMyPatients(!showOnlyMyPatients)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ring-2 ring-offset-2 ring-transparent focus:ring-brand-500 ${showOnlyMyPatients ? 'bg-brand-600' : 'bg-slate-200'}`}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showOnlyMyPatients ? 'translate-x-6' : 'translate-x-1'}`} />
                                </button>
                            </div>
                            <div className="h-4 w-[1px] bg-slate-100 hidden sm:block"></div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden sm:block">
                                Displaying {filteredProfiles.length} of {profiles.length} Clinical Profiles
                            </p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setStatusFilter('All'); setSupportFilter('All'); setShowOnlyMyPatients(false); }} className="text-slate-400 hover:text-brand-600 font-bold text-[10px] uppercase tracking-widest h-auto p-0">
                            Clear all filters
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Profiles Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                {filteredProfiles.map(profile => (
                    <Card key={profile.id} className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 hover:shadow-premium hover:-translate-y-1 transition-all group overflow-hidden">
                        <div className="p-8 h-full flex flex-col">
                            <div className="flex justify-between items-start mb-8">
                                <div className="h-16 w-16 rounded-2xl bg-slate-50 flex items-center justify-center text-2xl font-black text-slate-300 group-hover:bg-brand-50 group-hover:text-brand-600 transition-all border border-slate-100 shadow-inner">
                                    {profile.name?.charAt(0)}
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <Badge className={`rounded-xl px-4 py-1.5 text-[10px] font-black uppercase tracking-widest ${profile.status.includes('Active') ? 'bg-teal-50 text-teal-600 border-teal-100' :
                                            profile.status === 'Discharged' ? 'bg-slate-50 text-slate-400 border-slate-100' :
                                                'bg-amber-50 text-amber-600 border-amber-100'
                                        }`}>
                                        {profile.status}
                                    </Badge>
                                </div>
                            </div>

                            <div className="flex-1">
                                <Link to={`/Screening_test/app/profiles/${profile.id}`}>
                                    <h3 className="text-2xl font-black text-slate-900 group-hover:text-brand-600 transition-colors tracking-tight font-outfit uppercase">{profile.name}</h3>
                                </Link>
                                <p className="text-slate-400 font-bold text-sm mt-1 uppercase tracking-wider">{profile.age} Years • {profile.supportLevel}</p>

                                <div className="mt-8 space-y-4">
                                    <div className="flex items-center gap-3 bg-slate-50/50 p-3 rounded-2xl border border-slate-50">
                                        <Activity className="h-4 w-4 text-slate-400" />
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">Primary Diagnosis</p>
                                            <p className="text-sm font-bold text-slate-700 truncate w-48">{profile.diagnosis}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 bg-slate-50/50 p-3 rounded-2xl border border-slate-50">
                                        <Target className="h-4 w-4 text-slate-400" />
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">Client Query</p>
                                            <p className="text-sm font-bold text-slate-700 truncate w-48">{profile.pcMainConcern || 'Not Specified'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-10 pt-6 border-t border-slate-50 flex items-center justify-between">
                                <div className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-400 tracking-widest">
                                    <Calendar className="h-4 w-4" /> Activity 2h ago
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-10 w-10 rounded-xl bg-slate-50 text-slate-400 group-hover:bg-brand-600 group-hover:text-white transition-all shadow-sm"
                                    onClick={() => navigate(`/Screening_test/app/profiles/${profile.id}`)}
                                >
                                    <ChevronRight className="h-5 w-5" />
                                </Button>
                            </div>
                        </div>
                    </Card>
                ))}

                {filteredProfiles.length === 0 && (
                    <div className="col-span-full py-24 text-center">
                        <div className="h-20 w-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-slate-100">
                            <User className="h-10 w-10 text-slate-200" />
                        </div>
                        <h3 className="text-2xl font-black text-slate-900 font-outfit">No matches found</h3>
                        <p className="text-slate-400 font-medium mt-2">Try adjusting your filters or search terms.</p>
                        <Button variant="ghost" onClick={() => { setSearch(''); setStatusFilter('All'); setSupportFilter('All'); }} className="mt-6 text-brand-600 font-black text-xs uppercase tracking-widest hover:bg-brand-50">
                            Reset Dashboard
                        </Button>
                    </div>
                )}
            </div>

            {/* Registration Modal */}
            {isAddModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xl z-[100] flex items-center justify-center p-6 sm:p-12 overflow-y-auto">
                    <Card className="w-full max-w-2xl shadow-premium border-0 rounded-[3rem] overflow-hidden bg-white relative">
                        <button
                            onClick={() => setAddModalOpen(false)}
                            className="absolute top-8 right-8 h-10 w-10 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-colors z-20"
                        >
                            <X className="h-5 w-5" />
                        </button>

                        <div className="grid md:grid-cols-5 h-full min-h-[500px]">
                            <div className="md:col-span-2 bg-slate-900 p-10 text-white flex flex-col justify-between relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-full h-full bg-brand-600/10 blur-3xl rounded-full translate-y-1/2"></div>
                                <div className="relative z-10">
                                    <HeartPulse className="h-12 w-12 text-brand-500 mb-8" />
                                    <h2 className="text-4xl font-black font-outfit leading-tight mb-4">Patient Entry</h2>
                                    <p className="text-slate-400 font-medium leading-relaxed">Initialize a new clinical profile to begin tracking growth and interventions.</p>
                                </div>
                                <div className="relative z-10 pt-8">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="h-1 w-12 bg-brand-500 rounded-full"></div>
                                        <span className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-500">Secure Protocol</span>
                                    </div>
                                    <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest leading-relaxed">All clinical data is encrypted and HIPAA compliant.</p>
                                </div>
                            </div>

                            <form onSubmit={handleAdd} className="md:col-span-3 p-10 flex flex-col justify-center">
                                <div className="space-y-6">
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Full Legal Name</label>
                                        <input
                                            name="name"
                                            required
                                            placeholder="e.g. Samuel Rodriguez"
                                            className="w-full h-14 px-6 rounded-2xl bg-white border border-slate-200 focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 transition-all"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-6">
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Current Age</label>
                                            <input
                                                name="age"
                                                type="number"
                                                required
                                                placeholder="8"
                                                className="w-full h-14 px-6 rounded-2xl bg-white border border-slate-200 focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-1">Support Need</label>
                                            <select name="supportLevel" className="w-full h-14 px-5 rounded-2xl bg-white border border-slate-200 focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 appearance-none transition-all">
                                                <option value="Level 1">Level 1 (Mild)</option>
                                                <option value="Level 2">Level 2 (Moderate)</option>
                                                <option value="Level 3">Level 3 (Substantial)</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Initial Diagnosis / Clinical Marker</label>
                                        <input
                                            name="diagnosis"
                                            required
                                            placeholder="e.g. Down Syndrome, ASD Marker"
                                            className="w-full h-14 px-6 rounded-2xl bg-white border border-slate-200 focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Primary Concern / Client Query</label>
                                        <input
                                            name="pcMainConcern"
                                            placeholder="e.g. Speech delays, emotional regulation"
                                            className="w-full h-14 px-6 rounded-2xl bg-white border border-slate-200 focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 transition-all"
                                        />
                                    </div>
                                </div>
                                <div className="mt-12 flex gap-4">
                                    <Button type="button" variant="ghost" className="flex-1 h-14 rounded-2xl font-black text-xs uppercase tracking-widest text-slate-400 hover:bg-slate-50 transition-all" onClick={() => setAddModalOpen(false)}>Cancel</Button>
                                    <Button type="submit" className="flex-1 h-14 rounded-2xl font-black text-xs uppercase tracking-widest bg-brand-600 hover:bg-brand-700 text-white shadow-premium transition-all">Initialize Profile</Button>
                                </div>
                            </form>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}
